import type { ReactNode } from "react";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { cn } from "@volca/ui/lib/utils";
import { BlockList } from "./statement-block-list";
import {
  ItemActionContext,
  type StatementItemAction,
} from "./statement-item-action";
import { COLUMN } from "./statement-layout";
import type { PayStatement } from "./statement-schema";

export {
  referenceKey,
  STATEMENT_ITEM_ACTION_INTENT,
  STATEMENT_ITEM_MARK,
  statementRowKey,
} from "./statement-item-action";
export type {
  StatementItemAction,
  StatementItemActionIntent,
  StatementItemMark,
} from "./statement-item-action";
export type { StatementItemReference } from "./statement-item-reference";

// Renders a PayStatement as one flat, scannable surface — no nested cards. The
// statement is a single sheet: a header, the at-a-glance breakdown, then the
// line items as hairline-divided rows. Everything sits in one centered column
// so it uses the full width on a phone and stays readable on a wide screen.

interface StatementRendererProps {
  statement: PayStatement;
  footer?: ReactNode;
  /** Supply to put a "question this figure" action on every row. */
  itemAction?: StatementItemAction;
  /**
   * Whose pack this is, as its own titled block.
   *
   * Off where the surface around it already says: beside a conversation with the
   * technician, repeating their name is a second copy of the same fact and its
   * rule lands a few pixels off the neighbouring pane's.
   */
  showTitle?: boolean;
}

export const StatementRenderer = ({
  statement,
  footer,
  itemAction,
  showTitle = true,
}: StatementRendererProps) => (
  <ItemActionContext.Provider value={itemAction ?? null}>
    {/* The whole statement scrolls as one — the title scrolls away with the content
      rather than staying pinned. */}
    {/* `pb-6` inside the scroll: at the end of a long pack the last row would
        otherwise sit flush against whatever frames this. */}
    {/* Focusable, so a keyboard can scroll a pack that has no controls in it. */}
    <Stack
      gap={0}
      align="stretch"
      tabIndex={0}
      role="region"
      aria-label={statement.title}
      className="quiet-scrollbar h-full min-h-0 w-full overflow-y-auto bg-surface-raised pb-6 outline-none focus-visible:ring-3 focus-visible:ring-focus/50 focus-visible:ring-inset"
    >
      {showTitle && (
        <Stack className="w-full" gap={0} align="stretch">
          {/* The rule sits on the column, not on the page: full-bleed it ran the
            whole width of a desktop viewport while the pack it divides is a
            centred column, which reads as a stripe across empty space. */}
          <Stack
            align="start"
            gap={0}
            className={cn(COLUMN, "gap-0.5 border-b border-line py-4")}
          >
            <Text
              variant="title"
              className="min-w-0 truncate text-ink"
              title={statement.title}
            >
              {statement.title}
            </Text>
            {statement.subtitle && (
              <Text variant="caption" tone="muted">
                {statement.subtitle}
              </Text>
            )}
          </Stack>
        </Stack>
      )}

      {statement.blocks.length > 0 ? (
        <BlockList blocks={statement.blocks} />
      ) : (
        <Empty
          className={cn(COLUMN, emptyStateVariants({ layout: "collection" }))}
        >
          <EmptyHeader>
            <EmptyDescription>No pay to show for this period.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      {footer && (
        <Stack align="start" gap={4} className={cn("w-full", COLUMN, "pb-10")}>
          {footer}
        </Stack>
      )}
    </Stack>
  </ItemActionContext.Provider>
);
